import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

/* Image revealed like a curtain while scrolling, with internal parallax.
   The observer sits on the (unclipped) outer wrapper; the clip-path animates
   on an inner layer — otherwise a fully clipped element is never "in view". */
export function ParallaxImage({ src, alt = "", className = "", speed = 36, hoverScale = 1.18 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-speed, speed]);
  return (
    <motion.div
      ref={ref}
      className={`relative ${className}`}
      initial="hidden"
      whileInView="shown"
      viewport={{ once: true, amount: 0.2 }}
      data-testid="parallax-image"
    >
      <motion.div
        variants={{
          hidden: { clipPath: "inset(100% 0% 0% 0%)" },
          shown: { clipPath: "inset(0% 0% 0% 0%)" },
        }}
        transition={{ duration: 1.1, ease: EASE }}
        className="absolute inset-0 overflow-hidden rounded-[inherit]"
      >
        <motion.img
          src={src}
          alt={alt}
          style={{ y, scale: 1.16 }}
          whileHover={{ scale: hoverScale }}
          transition={{ duration: 1.2, ease: EASE }}
          className="img-warm h-full w-full object-cover"
          loading="lazy"
        />
      </motion.div>
    </motion.div>
  );
}

/* Title revealed word by word while scrolling (observer on the container). */
export function WordReveal({ text, className = "", delay = 0 }) {
  const words = text.split(" ");
  return (
    <motion.span
      className={className}
      initial="hidden"
      whileInView="shown"
      viewport={{ once: true, amount: 0.4 }}
    >
      {words.map((w, i) => (
        <span key={i} className="-mb-2 inline-block overflow-hidden pb-2 align-bottom">
          <motion.span
            className="inline-block"
            variants={{ hidden: { y: "112%" }, shown: { y: 0 } }}
            transition={{ duration: 0.7, ease: EASE, delay: delay + i * 0.045 }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
}


